/**
 * CharacterCreationScene — name + 12 Vocations (static order) + starter realm.
 * On Wake: writes registry + localStorage draft, then returns to TitleScene in
 * entering-world mode which triggers the seamless dissolve transition (AGE-1380)
 * into OverworldScene. Spec: docs/design/character-creation-scene.md.
 */
import * as Phaser from 'phaser';
import { RealmId, VocationKey } from '@game/shared';
import {
  CHARACTER_CREATION_SCENE_KEY,
  TITLE_SCENE_KEY,
} from './sceneKeys';
import {
  CHARACTER_DRAFT_STORAGE_KEY,
  NAME_MAX_LENGTH,
  buildDraft,
  buildRealmDisplayRows,
  buildVocationDisplayRows,
  normalizeCharacterName,
  serializeDraft,
} from './characterCreationDraft';

const COLOR_INK = '#3d2c1d';
const COLOR_INK_SOFT = '#7a5a3a';
const FONT = "'Almendra', serif";
const TILE_W = 170;
const TILE_H = 64;
const TILE_GAP = 8;

interface VocationTile {
  bg: Phaser.GameObjects.Rectangle;
  border: Phaser.GameObjects.Rectangle;
  key: VocationKey;
}

interface RealmRow {
  marker: Phaser.GameObjects.Text;
  id: RealmId;
}

export class CharacterCreationScene extends Phaser.Scene {
  public static readonly KEY = CHARACTER_CREATION_SCENE_KEY;

  private rawName = '';
  private nameLabel: Phaser.GameObjects.Text | null = null;
  private vocationKey: VocationKey | null = null;
  private realmId: RealmId = RealmId.VerdanaFields;
  private tiles: VocationTile[] = [];
  private realmRows: RealmRow[] = [];
  private footer: Phaser.GameObjects.Text | null = null;
  private wakeLabel: Phaser.GameObjects.Text | null = null;
  private wakeHit: Phaser.GameObjects.Rectangle | null = null;
  private vocationRows = buildVocationDisplayRows();
  private slotIndex = 0;

  constructor() {
    super({ key: CHARACTER_CREATION_SCENE_KEY });
  }

  init(data?: { slotIndex?: number }): void {
    this.slotIndex = data?.slotIndex ?? 0;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#f3e8c8');
    this.renderHeading();
    this.renderNameField();
    this.renderVocationGrid();
    this.renderFocusFooter();
    this.renderRealmPicker();
    this.renderWakeButton();
    this.renderEscapeHint();
    this.input.keyboard?.on('keydown', (e: KeyboardEvent) => this.onKey(e));
  }

  private renderHeading(): void {
    this.text(this.scale.width / 2, 38, 'Who will you be on the long morning?', 22, COLOR_INK);
  }

  private renderNameField(): void {
    const cx = this.scale.width / 2;
    const y = 76;
    this.text(cx, y, 'a name, if you like', 12, COLOR_INK_SOFT, 'italic');
    this.add
      .rectangle(cx, y + 22, 360, 26, 0xffffff, 1)
      .setStrokeStyle(1, 0xb78a55);
    this.nameLabel = this.text(cx, y + 22, '_', 14, COLOR_INK);
  }

  private renderVocationGrid(): void {
    const cols = 4;
    const gridW = cols * TILE_W + (cols - 1) * TILE_GAP;
    const startX = (this.scale.width - gridW) / 2 + TILE_W / 2;
    const startY = 142;

    this.vocationRows.forEach((row, idx) => {
      const col = idx % cols;
      const r = Math.floor(idx / cols);
      const x = startX + col * (TILE_W + TILE_GAP);
      const y = startY + r * (TILE_H + TILE_GAP);
      const bg = this.add
        .rectangle(x, y, TILE_W, TILE_H, 0xfff8e7, 1)
        .setInteractive({ useHandCursor: true });
      const border = this.add
        .rectangle(x, y, TILE_W, TILE_H, 0x000000, 0)
        .setStrokeStyle(1, 0xb78a55);
      this.text(x, y - 10, row.displayName, 14, COLOR_INK);
      this.text(x, y + 12, row.shortDescription, 11, COLOR_INK_SOFT);
      bg.on('pointerover', () => this.onHover(row.key));
      bg.on('pointerdown', () => this.onSelectVocation(row.key));
      this.tiles.push({ bg, border, key: row.key });
    });
  }

  private renderFocusFooter(): void {
    this.footer = this.text(
      this.scale.width / 2,
      350,
      'Twelve ways of being. Pick one. You will find out what it means.',
      12,
      COLOR_INK_SOFT,
      'italic',
    );
  }

  private renderRealmPicker(): void {
    const startX = this.scale.width / 2 - 120;
    const startY = 380;
    this.text(startX, startY, 'where to wake:', 12, COLOR_INK_SOFT).setOrigin(0, 0.5);
    buildRealmDisplayRows().forEach((row, idx) => {
      const y = startY + 18 + idx * 18;
      this.add
        .rectangle(startX, y, 240, 18, 0x000000, 0)
        .setOrigin(0, 0.5)
        .setInteractive({ useHandCursor: true })
        .on('pointerdown', () => this.onSelectRealm(row.id));
      const marker = this.text(startX, y, ' ', 12, '#c97f3a').setOrigin(0, 0.5);
      this.text(startX + 16, y, row.displayName, 12, COLOR_INK).setOrigin(0, 0.5);
      this.realmRows.push({ marker, id: row.id });
    });
    this.refreshRealms();
  }

  private renderWakeButton(): void {
    const cx = this.scale.width / 2;
    const y = this.scale.height - 36;
    this.wakeHit = this.add
      .rectangle(cx, y, 140, 32, 0xeed8a8, 1)
      .setStrokeStyle(1, 0xc97f3a)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.onWake());
    this.wakeLabel = this.text(cx, y, 'Wake', 18, COLOR_INK_SOFT);
    this.refreshWake();
  }

  private renderEscapeHint(): void {
    this.text(12, this.scale.height - 14, 'esc — return to title', 10, COLOR_INK_SOFT)
      .setOrigin(0, 1);
  }

  private text(
    x: number,
    y: number,
    str: string,
    size: number,
    color: string,
    style?: 'italic',
  ): Phaser.GameObjects.Text {
    const t = this.add
      .text(x, y, str, {
        fontFamily: FONT,
        fontSize: `${size}px`,
        color,
        ...(style ? { fontStyle: style } : {}),
      })
      .setOrigin(0.5, 0.5);
    return t;
  }

  private onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.scene.start(TITLE_SCENE_KEY);
      return;
    }
    if (event.key === 'Enter') {
      this.onWake();
      return;
    }
    if (event.key === 'Backspace') {
      this.rawName = this.rawName.slice(0, -1);
      this.nameLabel?.setText(this.rawName.length > 0 ? this.rawName : '_');
      return;
    }
    if (event.key.length === 1 && this.rawName.length < NAME_MAX_LENGTH) {
      this.rawName += event.key;
      this.nameLabel?.setText(this.rawName);
    }
  }

  private onHover(key: VocationKey): void {
    const row = this.vocationRows.find((r) => r.key === key);
    if (!row) return;
    this.footer?.setText(`${row.displayName} — "${row.shortDescription}"`);
  }

  private onSelectVocation(key: VocationKey): void {
    this.vocationKey = key;
    for (const tile of this.tiles) {
      const isSel = tile.key === key;
      tile.bg.setFillStyle(isSel ? 0xf5e0b8 : 0xfff8e7, 1);
      tile.border.setStrokeStyle(isSel ? 2 : 1, isSel ? 0xc97f3a : 0xb78a55);
    }
    this.refreshWake();
  }

  private onSelectRealm(id: RealmId): void {
    this.realmId = id;
    this.refreshRealms();
  }

  private refreshRealms(): void {
    for (const row of this.realmRows) {
      row.marker.setText(row.id === this.realmId ? '•' : ' ');
    }
  }

  private refreshWake(): void {
    const enabled = this.vocationKey !== null;
    this.wakeLabel?.setColor(enabled ? COLOR_INK : COLOR_INK_SOFT);
    this.wakeHit?.setFillStyle(enabled ? 0xeed8a8 : 0xefe6cd, 1);
  }

  private onWake(): void {
    if (this.vocationKey === null) return;
    const draft = buildDraft({
      name: normalizeCharacterName(this.rawName),
      vocation: this.vocationKey,
      realm: this.realmId,
      now: Date.now(),
    });
    this.registry.set('character:name', draft.name);
    this.registry.set('character:vocation', draft.vocation);
    this.registry.set('character:realm', draft.realm);
    this.registry.set('character:firstDay', true);
    try {
      localStorage.setItem(CHARACTER_DRAFT_STORAGE_KEY, serializeDraft(draft));
    } catch {
      // localStorage unavailable (private browsing) — registry-only is fine.
    }
    // Route back to TitleScene in entering-world mode — triggers the seamless
    // dissolve (AGE-1380) instead of the old ThresholdScene loading screen.
    this.scene.start(TITLE_SCENE_KEY, {
      enteringWorld: true,
      slotIndex: this.slotIndex,
      characterName: draft.name ?? '',
      vocation: draft.vocation,
      firstDay: true,
    });
  }
}
